import _ from "lodash";
import { eventDateFormatter, toStringFormatter } from "./formatters";
import Styles from "./CellClass.module.scss";
import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { renderPlayerNameWithLink, renderPlayerNameWithoutLink } from "./renderers";
import { generatePath, Link } from "react-router-dom";
import { ROUTES } from "src/utils/constants/routes";
import { useIsMobile } from "src/components/util/useBreakpoint";
import { dateComparator } from "src/utils/date-utils";
import * as Team from "src/state/modules/team";
import * as Eden from "src/eden/content";

export interface GridItem {
  person_id: string;
  team_id: string;
  name: string;
  // Set only by the player-profile per-game grid, where each row is an event. When present the
  // left-column cell links to that event's detail page instead of the player's own profile.
  eventID?: string;
  // Event start (per-game grid only); surfaced by the formatters as the `event` field that the
  // per-game Date column reads.
  date?: string;
  defense: Eden.Defense;
  offense: Eden.CoreBattingStats;
}

// Leading "Date" column for the per-game (player-profile) stats tables. Defined with top-level
// props so they survive StatsDataGrid's `{...col}` spread, and pinned + sorted ascending so the
// game log reads oldest → newest like the GameChanger app. Reads the `event` field the formatters
// emit from each row's event start.
export const EventDateColumn: StatsInfo = {
  name: "Date",
  field: "event",
  pinned: true,
  width: 100,
  cellStyle: { textAlign: "left" },
  sort: "asc",
  valueFormatter: eventDateFormatter,
  comparator: dateComparator,
};

/**
 * Replaces the column whose `name` matches with `override`, in place. Lets a single column be
 * tweaked (e.g. dropping a default sort) by name rather than by positional index, which is
 * brittle when the shared base column list is reordered.
 */
export const overrideColumn = (columns: StatsInfo[], name: string, override: StatsInfo): void => {
  const index = columns.findIndex((column) => column.name === name);
  if (index >= 0) {
    columns[index] = override;
  }
};

export const defaultGridOptions = {
  defaultColDef: {
    width: 100,
    suppressMovable: true,
    cellStyle: { fontWeight: "normal", textAlign: "center" },
  },
};

export const BasePlayerColumn: StatsInfo = {
  name: "Player",
  field: "player",
  width: 224,
  pinned: true,
  cellStyle: { textAlign: "left" },
  valueFormatter: toStringFormatter,
  cellRenderer: renderPlayerNameWithLink,
};

export type GridPlayer = {
  name: string;
  team_id: string;
  person_id: string;
  eventID?: string;
  general: {
    gp: number | null;
  };
  offense: Eden.CoreBattingStats;
  defense: Eden.Defense;
};

export interface PlayerStatsProps {
  opponent: string;
  date: string;
  eventID?: string;
  stats: Eden.StatData;
}

export function mergeForPlayerStatsGrid(player: Team.Player, stats: PlayerStatsProps[]) {
  let mergedPlayers = stats.map((game: PlayerStatsProps) => {
    return {
      name: game.opponent,
      date: game.date,
      eventID: game.eventID,
      team_id: player.team_id,
      person_id: player.id,
      general: game.stats.general,
      offense: game.stats.offense,
      defense: game.stats.defense,
    } as GridPlayer;
  });
  return mergedPlayers;
}

// Builds the synthetic "Team" row pinned to the bottom of every stats table from the season's
// team-total blob (Eden `stats_data.stats`). It deliberately omits person_id/team_id so the pinned
// Player cell renders plain text ("Team") instead of a player link. Reuses the same offense/defense
// shape as player rows, so it flows through every per-table formatter unchanged.
export function buildTeamTotalGridItem(stats: Eden.StatData): GridPlayer {
  return {
    name: "Team",
    team_id: "",
    person_id: "",
    general: stats.general,
    offense: stats.offense,
    defense: stats.defense,
  };
}

export function mergeSeasonStatsForGrid(players: Team.Player[], players_stats: Eden.Players) {
  let mergedPlayers = players.map((player: Team.Player) => {
    if (!players_stats[player.id]) return undefined;
    return {
      name: `${player.first_name} ${player.last_name}, #${player.number}`,
      team_id: player.team_id,
      person_id: player.id,
      general: players_stats[player.id].stats.general,
      offense: players_stats[player.id].stats.offense,
      defense: players_stats[player.id].stats.defense,
    } as GridPlayer;
  });
  return mergedPlayers.filter((value: GridPlayer | undefined, index: number) => {
    return value?.team_id !== undefined;
  });
}

// Rendered by AG-Grid as a React component so the name navigates via SPA routing (vs. the raw
// <a> the string renderer emits). Guards against rows without a real player (e.g. the per-game
// grid keys on opponent, not a person) by rendering nothing. Long names truncate with an ellipsis
// so the pinned name column can stay narrow on mobile (PG/PBR lookup links live on the Team roster
// and player profile, not here — they used to steal the horizontal room these stat columns need).
const truncateStyle = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// On phones, abbreviate the first name to an initial ("Nash Herring, #14" → "N Herring, #14"),
// like the GameChanger app, so the name fits the narrow pinned column without truncating.
const abbreviateFirstName = (name: string) => name.replace(/^(\S)\S*\s+/, "$1 ");

export const PlayerNameCell = (p: any) => {
  const isMobile = useIsMobile();
  const player = p.value;
  if (!player?.name) return null;

  // Player-profile per-game rows carry an eventID and show the opponent name; link the row to that
  // game's box score. Keep the full opponent name (don't abbreviate — that's only for people).
  if (player.eventID && player.teamID) {
    return (
      <Link
        style={truncateStyle}
        to={generatePath(ROUTES.teams.events.boxScore, { eid: player.eventID, id: player.teamID })}
      >
        {player.name}
      </Link>
    );
  }

  const displayName = isMobile ? abbreviateFirstName(player.name) : player.name;

  if (player.id && player.teamID && player.status === "active") {
    return (
      <Link style={truncateStyle} to={generatePath(ROUTES.teams.playerProfile, { pid: player.id, id: player.teamID })}>
        {displayName}
      </Link>
    );
  }
  return <span style={truncateStyle}>{displayName}</span>;
};

export const PlayerWithLinkColumn: StatsInfo = _.merge({}, BasePlayerColumn, { cellRenderer: PlayerNameCell });

// export const PlayerWithLinkColumn: StatsInfo = _.merge({}, BasePlayerColumn, {
//   columnProps: { cellRenderer: renderPlayerNameWithLink },
//   cellClass: Styles.playerCol,
// });

export const PlayerWithoutLinkColumn: StatsInfo = _.merge({}, BasePlayerColumn, {
  columnProps: { cellRenderer: renderPlayerNameWithoutLink },
  cellClass: Styles.playerCol,
});
